import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { ReadingProgress } from "../components/ReadingProgress";
import { SocialShare } from "../components/SocialShare";
import { ArticleCard } from "../components/ArticleCard";
import { CATEGORY_LABELS } from "../lib/constants";

export default function ArticlePage() {
    const { id } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        setLoading(true);
        setNotFound(false);
        api.get(`/articles/${id}`)
            .then((r) => setArticle(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-24 text-center">
                <p className="inline-flex items-center gap-2 text-sm font-medium text-dim">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-dot" />
                    Loading article…
                </p>
            </div>
        );
    }
    if (notFound || !article) {
        return (
            <div className="max-w-md mx-auto px-4 py-24" data-testid="article-not-found">
                <div className="panel p-12 text-center">
                    <h1 className="font-display text-2xl font-bold tracking-[-0.02em]">Article not found</h1>
                    <Link
                        to="/"
                        className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-accent link-underline"
                    >
                        <ArrowLeft size={13} /> Back home
                    </Link>
                </div>
            </div>
        );
    }

    const published = article.published_at ? new Date(article.published_at) : null;

    return (
        <>
            <ReadingProgress />
            <article className="py-10" data-testid="article-page">
                <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="max-w-3xl mx-auto text-center">
                        <Link
                            to={`/category/${article.category}`}
                            className="chip !px-3 !py-1.5 !text-xs hover:bg-accent hover:text-accent-foreground transition-colors duration-150"
                        >
                            {CATEGORY_LABELS[article.category] || article.category}
                        </Link>
                        <h1
                            className="font-display text-3xl sm:text-4xl lg:text-[3.25rem] font-bold leading-[1.08] tracking-[-0.035em] mt-6"
                            data-testid="article-title"
                        >
                            {article.title}
                        </h1>
                        {article.excerpt && (
                            <p className="mt-6 text-lg text-dim leading-relaxed max-w-2xl mx-auto">{article.excerpt}</p>
                        )}
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 font-mono text-xs text-dim">
                            {article.author && <span className="text-foreground">{article.author}</span>}
                            {published && <span className="w-1 h-1 rounded-full bg-line-strong" />}
                            {published && (
                                <time>{published.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</time>
                            )}
                            {article.wordcount > 0 && (
                                <>
                                    <span className="w-1 h-1 rounded-full bg-line-strong" />
                                    <span className="text-accent">{Math.max(1, Math.round(article.wordcount / 200))} min read</span>
                                </>
                            )}
                        </div>
                        <div className="rule mt-8" />
                    </div>

                    {/* Hero image */}
                    {article.thumbnail && (
                        <div className="max-w-4xl mx-auto mt-10">
                            <img
                                src={article.thumbnail}
                                alt={article.title}
                                className="w-full aspect-video object-cover rounded-xl border border-line bg-surface-raised shadow-card"
                            />
                        </div>
                    )}

                    {/* Body + share rail */}
                    <div className="max-w-3xl mx-auto mt-12 relative">
                        <div className="hidden lg:block absolute -left-20 top-0 sticky-share">
                            <div className="sticky top-24">
                                <SocialShare title={article.title} orientation="vertical" />
                            </div>
                        </div>

                        <div className="lg:hidden mb-8 flex items-center gap-3">
                            <span className="eyebrow text-dim">Share</span>
                            <SocialShare title={article.title} orientation="horizontal" />
                        </div>

                        <div
                            className="article-body font-sans"
                            data-testid="article-body"
                            dangerouslySetInnerHTML={{ __html: article.body }}
                        />

                        <div className="mt-12 panel p-5">
                            <a
                                href={article.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-flex items-center gap-2 text-sm font-semibold text-accent"
                            >
                                Read the original on The Guardian
                                <ArrowUpRight size={14} className="transition-transform duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Related */}
                {article.related?.length > 0 && (
                    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-24">
                        <div className="flex items-center justify-between gap-4 mb-6">
                            <h2 className="font-display text-xl sm:text-[1.6rem] font-extrabold tracking-[-0.025em] text-foreground">
                                More in {CATEGORY_LABELS[article.category] || article.category}
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {article.related.map((a) => (
                                <ArticleCard key={a.id} article={a} />
                            ))}
                        </div>
                    </div>
                )}
            </article>
        </>
    );
}
